import { ReviewSummaryResponse } from '|/services/purchase-api';

export interface ManufacturerWarrantyRow {
  key: 'basic' | 'powertrain';
  text: string;
  isExpired: boolean;
}

type Vehicle = NonNullable<ReviewSummaryResponse['vehicle']>;

interface WarrantyInput {
  key: ManufacturerWarrantyRow['key'];
  name: string;
  months?: number;
  miles?: number;
  remainingMonths?: number;
  remainingMiles?: number;
}

const formatMiles = (miles: number) =>
  miles % 1000 === 0 ? `${miles / 1000}k mi.` : `${miles.toLocaleString('en-US')} mi.`;

const formatTerms = (months?: number, miles?: number) =>
  [months ? `${months} mo.` : null, miles ? formatMiles(miles) : null].filter(Boolean).join(' / ');

const toRow = ({
  key,
  name,
  months,
  miles,
  remainingMonths,
  remainingMiles
}: WarrantyInput): ManufacturerWarrantyRow | null => {
  if (remainingMonths === 0 || remainingMiles === 0) {
    return { key, text: `${name} warranty has expired`, isExpired: true };
  }

  const terms = formatTerms(months, miles);
  return terms ? { key, text: `${name} Limited: ${terms}`, isExpired: false } : null;
};

/**
 * The `manufacturer*Warranty*` fields carry the vehicle's original factory terms, which is what
 * the row displays; the `remaining*Warranty*` fields only decide whether that coverage has run
 * out. Zero remaining months or miles means expired — an absent `remaining*` value is unknown,
 * not expired, so the row keeps rendering its terms.
 *
 * A vehicle whose basic and powertrain coverage are both gone gets an empty result, meaning
 * "hide the Factory Warranty block entirely" rather than "render two expired rows".
 */
export const deriveManufacturerWarranty = (vehicle?: Vehicle | null): ManufacturerWarrantyRow[] => {
  if (!vehicle) return [];

  const rows = [
    toRow({
      key: 'basic',
      name: 'Basic',
      months: vehicle.manufacturerBasicWarrantyMonths,
      miles: vehicle.manufacturerBasicWarrantyMiles,
      remainingMonths: vehicle.remainingBasicWarrantyMonths,
      remainingMiles: vehicle.remainingBasicWarrantyMiles
    }),
    toRow({
      key: 'powertrain',
      name: 'Powertrain',
      months: vehicle.manufacturerDriveTrainWarrantyMonths,
      miles: vehicle.manufacturerDriveTrainWarrantyMiles,
      remainingMonths: vehicle.remainingDriveTrainWarrantyMonths,
      remainingMiles: vehicle.remainingDriveTrainWarrantyMiles
    })
  ].filter((row): row is ManufacturerWarrantyRow => row !== null);

  return rows.every(row => row.isExpired) ? [] : rows;
};

export default deriveManufacturerWarranty;
